import { Cell } from "./Cell";
import { ApplicantOrInvitee } from "../types";
import { Show } from "shared/components/helpers/Show";
import { Link } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicationStatus,
  Invitee,
  PartnerFeatures
} from "shared/types";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { getFullName } from "shared/utils/get-full-name";
import { compactNumber } from "shared/utils";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { useChatContext } from "stream-chat-react";
import { VerificationProgress } from "./VerificationProgress";
import { hasActionInManualApplication } from "views/applications-id/helpers";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { getApplicantStatus } from "utils/get-applicant-status";
import { ApplicationActionsMenu } from "shared/components/ApplicationActionsMenu";
import { ApplicationActionsMenuVariant } from "shared/components/ApplicationActionsMenu/types";
import { getApplicantRole } from "utils/getApplicantRole";

const getOptionalStringField = (
  obj: object,
  key: string
): string | undefined => {
  return key in obj
    ? (((obj as Record<string, unknown>)[key] as string) ?? undefined)
    : undefined;
};

const resolveNameFields = (
  applicant: ApplicantOrInvitee,
  pendingMagicLink?: Invitee
) => ({
  applicantFirstName: getOptionalStringField(applicant, "first_name"),
  applicantLastName: getOptionalStringField(applicant, "last_name"),
  applicantMiddleName: getOptionalStringField(applicant, "middle_name"),
  inviteFirstName: pendingMagicLink
    ? getOptionalStringField(pendingMagicLink, "first_name")
    : undefined,
  inviteLastName: pendingMagicLink
    ? getOptionalStringField(pendingMagicLink, "last_name")
    : undefined,
  inviteMiddleName: pendingMagicLink
    ? getOptionalStringField(pendingMagicLink, "middle_name")
    : undefined
});

const getApplicantNameOrEmail = (
  applicant: ApplicantOrInvitee,
  pendingMagicLink?: Invitee
) => {
  const {
    applicantFirstName,
    applicantLastName,
    applicantMiddleName,
    inviteFirstName,
    inviteLastName,
    inviteMiddleName
  } = resolveNameFields(applicant, pendingMagicLink);

  const hasApplicantName = Boolean(
    applicantFirstName || applicantLastName || applicantMiddleName
  );
  const hasInviteName = Boolean(
    inviteFirstName || inviteLastName || inviteMiddleName
  );

  if (hasApplicantName && applicantFirstName && applicantLastName)
    return getFullName({
      firstName: applicantFirstName,
      lastName: applicantLastName,
      middleName: applicantMiddleName,
      short: true
    });

  if (hasInviteName && inviteFirstName && inviteLastName)
    return getFullName({
      firstName: inviteFirstName,
      lastName: inviteLastName || pendingMagicLink?.email || "",
      middleName: inviteMiddleName || undefined,
      short: true
    });

  return pendingMagicLink?.email || applicant.email;
};

const getCompletionProgress = (
  applicant: ApplicantOrInvitee
): [number, number] => {
  // If application is finished or submitted -> simulate 100% completion
  // There are cases when count comes incorrectly from the backend,
  if (
    [ApplicantStatus.Finished, ApplicantStatus.Submitted].includes(
      applicant.application_status
    )
  )
    return [10, 10];

  if ("finished_verifications" in applicant)
    return [
      applicant.finished_verifications.finished_steps,
      applicant.finished_verifications.total_steps
    ];

  // if applicant is invitee -> simulate 0%
  return [0, 10];
};

export const ApplicantHeaderCell = ({
  applicant,
  applicationId,
  pendingMagicLink,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setSetPrimaryModal,
  setRefundModal,
  setResendInviteModal,
  setDocumentRequestModal,
  applicationStatus,
  allowCoSigner,
  allowOccupant,
  setManualApplicationOpen
}: {
  applicant: ApplicantOrInvitee;
  applicationId?: string;
  pendingMagicLink?: Invitee;
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setSetPrimaryModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  setDocumentRequestModal: Dispatch<SetStateAction<string | undefined>>;

  setResendInviteModal?: Dispatch<SetStateAction<Invitee | undefined>>;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  setManualApplicationOpen: (applicantId: number) => void;
}) => {
  const { permissions, type, partnerFeatures } = useAuthContext();
  const { adminPrivileges } = type;
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: disable chat for manual applicants
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  const { client } = useChatContext();

  const isApplicantWithChat = "verifications" in applicant && hasChat;
  const [unreads, setUnreads] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- client can be undefined at runtime outside <Chat>
    if (!isApplicantWithChat || !client) return;

    let cancelled = false;
    const channel = client.channel("messaging", `chat-id-${applicant.id}`);

    channel
      .query()
      .then(() => {
        if (!cancelled) setUnreads(channel.countUnread());
      })
      .catch(() => {});

    const subscription = client.on((event) => {
      if (!event.cid || event.cid !== channel.cid) return;

      if (event.type === "notification.message_new") {
        channel
          .query()
          .then(() => {
            if (!cancelled) setUnreads(channel.countUnread());
          })
          .catch(() => {});
        return;
      }

      setUnreads(channel.countUnread());
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [isApplicantWithChat, client, applicant.id]);

  const isApplicant = "verifications" in applicant;
  const isManual = "is_manual" in applicant && applicant.is_manual;
  const isPrimary =
    "is_primary" in applicant ? applicant.is_primary : undefined;

  const effectiveStatus = getApplicantStatus({
    applicant,
    pendingMagicLink
  });

  const chatLinkTo = isApplicant
    ? `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=chat`
    : "";

  const [finished, total] = useMemo(
    () => getCompletionProgress(applicant),
    [applicant]
  );

  const displayName = getApplicantNameOrEmail(applicant, pendingMagicLink);

  return (
    <Cell className="!h-[202px] relative w-full" isDataCell>
      <div className="w-full mb-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-content-1000 truncate max-w-[240px]">
            {displayName}
          </h3>
          <ApplicationActionsMenu
            variant={ApplicationActionsMenuVariant.Group}
            contentClassName="z-[12]"
            triggerClassName="border-none !shadow-none"
            borderLess={false}
            applicant={applicant}
            pendingMagicLink={pendingMagicLink}
            onCancelClick={(applicantId) =>
              setCancelApplicantModal(applicantId)
            }
            onUncancelClick={(applicantId) =>
              setUncancelApplicantModal(applicantId)
            }
            onChangeRoleClick={(applicant) =>
              setChangeRoleModal(applicant as ApplicantOrInvitee)
            }
            onSetPrimaryClick={(applicant) =>
              setSetPrimaryModal(applicant as ApplicantOrInvitee)
            }
            onDocumentRequest={(applicantId) =>
              setDocumentRequestModal(applicantId)
            }
            onRefundClick={(applicantId) => setRefundModal(applicantId)}
            onResendInviteClick={(invite) => setResendInviteModal?.(invite)}
            applicationStatus={applicationStatus}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            hasAdminPrivileges={adminPrivileges}
            permissions={{
              canChangeApplicantRole: permissions.canChangeApplicantRole,
              canCancelApplications: permissions.canCancelApplications,
              canResendInvitation: permissions.canResendInvitation,
              canRefundApplication: permissions.canRefundApplication
            }}
          />
        </div>
        <div className="flex justify-between items-center text-content-400 text-sm my-1">
          <p>{getApplicantRole(applicant.role, isPrimary)}</p>
          <p className="font-light text-content-300">
            {isManual ? "Manual " : "Self-serve "}application
          </p>
        </div>

        <VerificationProgress
          className="my-6"
          status={effectiveStatus || ApplicantStatus.Pending}
          completedVerifications={finished}
          totalVerifications={total}
        />

        {isManual &&
        !(
          effectiveStatus &&
          [ApplicantStatus.Canceled, ApplicantStatus.Submitted].includes(
            effectiveStatus
          )
        ) &&
        hasActionInManualApplication({
          ...applicant,
          verifications: applicant.verifications.flat()
        }) ? (
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            className="mt-2 w-full"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setManualApplicationOpen(Number(applicant.id));
            }}
          >
            {applicant.application_started_at
              ? "Complete application"
              : "Start application"}
          </Button>
        ) : null}
        {isApplicant &&
        !(
          applicant.is_manual &&
          [
            ApplicantStatus.Pending,
            ApplicantStatus.Finished,
            ApplicantStatus.Started
          ].includes(applicant.application_status)
        ) ? (
          <div className="flex items-center gap-2">
            <Button size={ButtonSize.SM} className="w-full">
              Review
            </Button>
            <Show
              if={
                hasChat &&
                isApplicant &&
                !applicant.is_manual &&
                applicant.application_started_at
              }
            >
              <Link to={chatLinkTo} className="relative shrink-0 block">
                <IconButton
                  HugeIcon={BubbleChatIcon}
                  size={IconButtonSize.SM}
                  className="block"
                />
                {unreads ? (
                  <div className="bg-primary-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center absolute -top-1.5 -right-1 outline outline-white">
                    {compactNumber(unreads)}
                  </div>
                ) : null}
              </Link>
            </Show>
          </div>
        ) : null}
      </div>
    </Cell>
  );
};
